import { makeAutoObservable } from 'mobx';

import type { PublishRecordService } from '@src/services/publish/PublishRecordService';
import type { PublishRecord } from '@src/types.ts/publishRecord';

/** A Dato video id — the key each clip's prior renders are cached under. */
type VideoId = string;

/** A Dato story id — the key the clips grid's one-shot fetch is cached under. */
type StoryId = string;

/**
 * Session cache of a video's *historical* PublishRecords, keyed by videoId.
 * Backs the clip-preview Downloads tab and Publish & Share panel, which both
 * read a clip's prior renders — without this they'd each fire their own fetch.
 * An in-flight promise per videoId deduplicates concurrent callers so the two
 * surfaces share one request.
 *
 * Scope is the clip-preview modal session: a plain fetch cache, no TTL or
 * eviction. `FullClipModalContent` clears the clip's variant ids on close, so
 * every reopen re-fetches fresh (matching the freshness the old per-mount hook
 * cache gave for free).
 *
 * Live / in-flight status is deliberately NOT held here — that stays in
 * `PublishTrackingStore`, which the consuming hook overlays on top of this
 * cache at read time. Keeping the two separate avoids two stores owning the
 * same records. Modelled on `ClipPreviewStore` (cache + in-flight dedupe; the
 * service is passed in so the store stays dependency-free).
 *
 * `storyRecords` is a SECOND, story-scoped cache for the Content Studio clips
 * grid (PLAT-11591). It deliberately does not share `cachedRecords`' key space:
 * the modal's clear-on-close exists so a reopened preview re-fetches, and
 * sharing keys would blank the grid behind the modal — every dubbed clip
 * silently dropping back to "Draft" the moment its preview was closed.
 *
 * Kept honest two ways, because they answer different staleness:
 * `PublishTrackingStore.trackRecords` promotes each record it sees finish into
 * it (`mergeStoryRecord`), so a publish made *here* is in history the moment it
 * lands rather than only in the live overlay — which `initializeWithStory`
 * resets on every story entry, and `seedForStory` restores in-flight records
 * alone. And `initializeWithStory` drops the story being loaded
 * (`clearStory`), which is the only thing that picks up a publish made in
 * another tab or by someone else. Consumers re-fetch on a missing entry, so
 * neither depends on running before or after the grid mounts.
 */
export class PublishRecordsStore {
  cachedRecords = new Map<VideoId, PublishRecord[]>();
  inflight = new Map<VideoId, Promise<PublishRecord[]>>();

  storyRecords = new Map<StoryId, PublishRecord[]>();
  storyInflight = new Map<StoryId, Promise<PublishRecord[]>>();
  /** Which video ids each story's cache has actually been asked for. */
  storyFetchedIds = new Map<StoryId, Set<VideoId>>();

  constructor() {
    makeAutoObservable(this, { inflight: false, storyInflight: false });
  }

  /**
   * Populate the cache for a videoId (no-op on cache or in-flight hit). Fire
   * from an effect; read the result reactively via `getRecords`. A failed fetch
   * caches `[]` (treated as "no prior renders") so `getRecords` resolves and the
   * consumer stops loading — the clear-on-close retries on the next open.
   */
  ensure(
    videoId: VideoId,
    service: PublishRecordService,
  ): Promise<PublishRecord[]> {
    const cached = this.cachedRecords.get(videoId);
    if (cached) return Promise.resolve(cached);

    const existing = this.inflight.get(videoId);
    if (existing) return existing;

    const promise = service
      .getRecordsForVideos([videoId])
      .catch(() => [] as PublishRecord[])
      .then(records => {
        this.cachedRecords.set(videoId, records);
        return records;
      })
      .finally(() => {
        this.inflight.delete(videoId);
      });

    this.inflight.set(videoId, promise);
    return promise;
  }

  /** Cached records for a videoId, or `undefined` if not yet fetched. */
  getRecords(videoId: VideoId): PublishRecord[] | undefined {
    return this.cachedRecords.get(videoId);
  }

  /**
   * Populate the story-scoped cache: every publish record for the given video
   * ids, in ONE request. The clips grid renders a story's clips unpaginated, so
   * per-clip `ensure` calls would be one request per card —
   * `getRecordsForVideos` already batches on `videoId: { in: … }`, which is the
   * whole reason this is affordable.
   *
   * INCREMENTAL by video id, because two callers ask for different slices of
   * the same story: the grid asks for every clip, the clip preview for one
   * clip's variants (plus any variant created since). Keying the cache on the
   * story alone would let whichever ran first satisfy the other from a partial
   * set. Only ids never asked for are fetched, and their records merge in — so
   * the modal opened over a loaded grid costs no request at all.
   *
   * A failed fetch still marks its ids fetched, so consumers stop loading and
   * fall back to the legacy fields (the pre-PLAT-11591 behaviour) rather than
   * re-requesting on every render.
   */
  ensureForStory(
    storyId: StoryId,
    videoIds: VideoId[],
    service: PublishRecordService,
  ): Promise<PublishRecord[]> {
    const fetchedIds = this.storyFetchedIds.get(storyId);
    const missing = videoIds.filter(id => id && !fetchedIds?.has(id));
    if (missing.length === 0) {
      return Promise.resolve(this.storyRecords.get(storyId) ?? []);
    }

    // One in-flight request per story: a second caller arriving mid-flight
    // chains onto it and then re-runs, picking up whatever ids it still needs.
    const existing = this.storyInflight.get(storyId);
    if (existing) {
      return existing.then(() =>
        this.ensureForStory(storyId, videoIds, service),
      );
    }

    const promise = service
      .getRecordsForVideos(missing)
      .catch(() => [] as PublishRecord[])
      .then(records => {
        const merged = new Map<string, PublishRecord>();
        for (const record of this.storyRecords.get(storyId) ?? []) {
          merged.set(record.id, record);
        }
        for (const record of records) merged.set(record.id, record);

        const all = Array.from(merged.values());
        this.storyRecords.set(storyId, all);
        this.storyFetchedIds.set(
          storyId,
          new Set([...(this.storyFetchedIds.get(storyId) ?? []), ...missing]),
        );
        return all;
      })
      .finally(() => {
        this.storyInflight.delete(storyId);
      });

    this.storyInflight.set(storyId, promise);
    return promise;
  }

  /** Cached story records, or `undefined` if not yet fetched. */
  getStoryRecords(storyId: StoryId): PublishRecord[] | undefined {
    return this.storyRecords.get(storyId);
  }

  /** Drop cache entries — e.g. the clip's variant ids on modal close. */
  clear(...videoIds: VideoId[]): void {
    for (const id of videoIds) this.cachedRecords.delete(id);
  }

  /**
   * Drop a story's grid cache, so re-entering the story re-fetches. Safe to call
   * at any point: a consumer whose entry disappears re-fires `ensureForStory`
   * (`useStoryClipPublishRecords` keys its effect on the entry's absence), so
   * this cannot strand an already-mounted grid on an empty set.
   */
  clearStory(storyId: StoryId): void {
    this.storyRecords.delete(storyId);
    this.storyFetchedIds.delete(storyId);
  }

  /**
   * Upsert a record into its story's cache — how a publish made in this session
   * reaches history without a refetch.
   *
   * Deliberately does NOT touch `storyFetchedIds`: the entry this creates for a
   * story whose grid was never loaded is one record, not an answer, and marking
   * ids fetched would let it satisfy `ensureForStory` as if it were the whole
   * set.
   */
  mergeStoryRecord(record: PublishRecord): void {
    const storyId = record.storyId;
    if (!storyId) return;

    const existing = this.storyRecords.get(storyId) ?? [];
    const index = existing.findIndex(r => r.id === record.id);
    // Replaced wholesale rather than mutated: the array is read in the render
    // path of `observer` components.
    const next = [...existing];
    if (index >= 0) next[index] = record;
    else next.push(record);
    this.storyRecords.set(storyId, next);
  }
}
